import type {
  ReportGenerationAllowedOperator,
  ReportGenerationAuthorityFieldMetadata,
  ReportGenerationAuthorityScope,
  ReportGenerationAuthorityStatus,
  ReportGenerationAuthorityTemplateInput,
} from "./reportGenerationFieldAuthority";
import {
  TUITION_REPORT_GENERATION_ENTITY_AUTHORITY_INPUTS,
  TUITION_REPORT_GENERATION_TEMPLATE_AUTHORITY_INPUTS,
} from "./tuitionReportFieldAuthority.generated.js";
import {
  YSTNJ_SCHOLARSHIP_WORKSHEET_COLUMNS,
  YSTNJ_SCHOLARSHIP_WORKSHEET_ENTITY_ID,
  YSTNJ_SCHOLARSHIP_WORKSHEET_TEMPLATE_ID,
} from "./ystnjScholarshipWorksheetReport";

export type ReportGenerationShellPersona =
  | "parent"
  | "staff"
  | "administrator"
  | "super-administrator"
  | "guest";

export type ReportGenerationModuleId =
  | "assistant"
  | "run-reports"
  | "admissions"
  | "attendance"
  | "control-plane"
  | "financial-aid"
  | "payroll"
  | "tuition"
  | "students-families"
  | "faculty"
  | "settings";

export type ReportGenerationShellModule = {
  id: ReportGenerationModuleId;
  routeRoot: string;
  allowedPersonas: ReportGenerationShellPersona[];
};

export type ReportGenerationCognitoProfile = {
  permissions?: readonly string[] | null;
  groups?: readonly string[] | null;
  isSuperAdmin?: boolean | null;
};

export type ReportGenerationFieldType =
  | "text"
  | "number"
  | "date"
  | "enum"
  | "boolean";
export type ReportGenerationPermissionKey =
  | "perm_view_students_families"
  | "perm_view_financial_info"
  | "perm_view_admissions"
  | "perm_view_financial_aid";

export type ReportGenerationFieldDefinition = {
  id: string;
  label: string;
  type: ReportGenerationFieldType;
  enumValues?: string[];
  authority?: ReportGenerationAuthorityFieldMetadata;
  allowedOperators?: ReportGenerationAllowedOperator[];
  sortable?: boolean;
  selectable?: boolean;
  unsupportedReason?: string;
};

export type ReportGenerationFieldCategoryDefinition = {
  name: string;
  fields: ReportGenerationFieldDefinition[];
};

export type ReportGenerationDataSourceId =
  | "domainStudentsFamiliesApi"
  | "domainAdmissionsApi"
  | "domainFinancialAidApi";

export type ReportGenerationEntityDefinition = {
  id: string;
  label: string;
  permission: ReportGenerationPermissionKey;
  moduleLabel:
    | "Students & Families"
    | "Tuition"
    | "Admissions"
    | "Financial Aid";
  moduleId: ReportGenerationModuleId;
  routeRoot: string;
  dataSourceId: ReportGenerationDataSourceId;
  authorityScope?: ReportGenerationAuthorityScope;
  authorityStatus?: ReportGenerationAuthorityStatus;
  fixedFieldSelection?: boolean;
  joinFields?: string[];
  categories: ReportGenerationFieldCategoryDefinition[];
};

export type ReportGenerationTemplateDefinition = {
  id: string;
  name: string;
  description: string;
  moduleLabel:
    | "Tuition"
    | "Financial Aid"
    | "Admissions"
    | "Students & Families"
    | "Cross-Module";
  authorityScope?: ReportGenerationAuthorityScope;
  authorityStatus?: ReportGenerationAuthorityStatus;
  entities: string[];
  fields: string[];
  conditions?: {
    id: string;
    fieldId: string;
    fieldLabel: string;
    fieldType: ReportGenerationFieldType;
    operator: string;
    value: string;
  }[];
  sorts?: {
    fieldId: string;
    fieldLabel: string;
    direction: "asc" | "desc";
  }[];
};

export type ReportGenerationGrantSource =
  | "claim"
  | "derived-persona"
  | "unmodeled";
export type ReportGenerationGrantStatus =
  | "granted-claim"
  | "granted-derived"
  | "denied-claim"
  | "denied-derived"
  | "unmodeled";

export type ReportGenerationPermissionResolution = {
  permission: string;
  granted: boolean;
  status: ReportGenerationGrantStatus;
  source: ReportGenerationGrantSource;
  moduleId: ReportGenerationModuleId | null;
  routeRoot: string | null;
  allowedPersonas: ReportGenerationShellPersona[];
  reason: string;
};

export type ReportGenerationEntityAccess = ReportGenerationEntityDefinition & {
  permissionResolution: ReportGenerationPermissionResolution;
  accessible: boolean;
};

export type ReportGenerationTemplateAccess =
  ReportGenerationTemplateDefinition & {
    accessible: boolean;
    blockedEntityIds: string[];
    blockedReasons: string[];
  };

type ReportGenerationPermissionPolicy = {
  permission: ReportGenerationPermissionKey;
  source: Extract<ReportGenerationGrantSource, "claim" | "derived-persona">;
  moduleId: ReportGenerationModuleId;
  allowedPersonas: ReportGenerationShellPersona[];
  reasonWhenDenied: string;
};

const STAFF_ROUTE_PERSONAS: ReportGenerationShellPersona[] = [
  "staff",
  "administrator",
  "super-administrator",
];

export const REPORT_GENERATION_SHELL_MODULES: ReportGenerationShellModule[] = [
  {
    id: "assistant",
    routeRoot: "/school/assistant",
    allowedPersonas: STAFF_ROUTE_PERSONAS,
  },
  {
    id: "run-reports",
    routeRoot: "/school/run-reports",
    allowedPersonas: STAFF_ROUTE_PERSONAS,
  },
  {
    id: "admissions",
    routeRoot: "/school/admissions",
    allowedPersonas: STAFF_ROUTE_PERSONAS,
  },
  {
    id: "attendance",
    routeRoot: "/school/attendance",
    allowedPersonas: STAFF_ROUTE_PERSONAS,
  },
  {
    id: "control-plane",
    routeRoot: "/super-administrator/control-plane",
    allowedPersonas: ["super-administrator"],
  },
  {
    id: "financial-aid",
    routeRoot: "/school/financialaid",
    allowedPersonas: STAFF_ROUTE_PERSONAS,
  },
  {
    id: "payroll",
    routeRoot: "/school/payroll",
    allowedPersonas: ["administrator", "super-administrator"],
  },
  {
    id: "tuition",
    routeRoot: "/school/tuition",
    allowedPersonas: STAFF_ROUTE_PERSONAS,
  },
  {
    id: "students-families",
    routeRoot: "/school/students",
    allowedPersonas: STAFF_ROUTE_PERSONAS,
  },
  {
    id: "faculty",
    routeRoot: "/school/faculty",
    allowedPersonas: STAFF_ROUTE_PERSONAS,
  },
  {
    id: "settings",
    routeRoot: "/school/settings",
    allowedPersonas: ["administrator", "super-administrator"],
  },
];

const MODULES_BY_ID = new Map<
  ReportGenerationModuleId,
  ReportGenerationShellModule
>(REPORT_GENERATION_SHELL_MODULES.map((module) => [module.id, module]));

const requireModule = (
  moduleId: ReportGenerationModuleId,
): ReportGenerationShellModule => {
  const module = MODULES_BY_ID.get(moduleId);
  if (!module) {
    throw new Error(`Missing shell module configuration for ${moduleId}.`);
  }
  return module;
};

const MODULE_SOURCE_METADATA: Record<
  ReportGenerationEntityDefinition["moduleLabel"],
  {
    moduleId: ReportGenerationModuleId;
    dataSourceId: ReportGenerationDataSourceId;
  }
> = {
  "Students & Families": {
    moduleId: "students-families",
    dataSourceId: "domainStudentsFamiliesApi",
  },
  Tuition: {
    moduleId: "tuition",
    dataSourceId: "domainStudentsFamiliesApi",
  },
  Admissions: {
    moduleId: "admissions",
    dataSourceId: "domainAdmissionsApi",
  },
  "Financial Aid": {
    moduleId: "financial-aid",
    dataSourceId: "domainFinancialAidApi",
  },
};

const buildEntityDefinition = (
  input: Omit<
    ReportGenerationEntityDefinition,
    "moduleId" | "routeRoot" | "dataSourceId"
  >,
): ReportGenerationEntityDefinition => {
  const moduleMetadata = MODULE_SOURCE_METADATA[input.moduleLabel];
  const shellModule = requireModule(moduleMetadata.moduleId);
  return {
    ...input,
    moduleId: moduleMetadata.moduleId,
    routeRoot: shellModule.routeRoot,
    dataSourceId: moduleMetadata.dataSourceId,
  };
};

const worksheetColumnKindToFieldType = (
  kind: (typeof YSTNJ_SCHOLARSHIP_WORKSHEET_COLUMNS)[number]["kind"],
): ReportGenerationFieldType => {
  if (kind === "currency" || kind === "integer" || kind === "percentage") {
    return "number";
  }
  return kind;
};

const REPORT_GENERATION_PERMISSION_POLICIES: Record<
  ReportGenerationPermissionKey,
  ReportGenerationPermissionPolicy
> = {
  perm_view_students_families: {
    permission: "perm_view_students_families",
    source: "derived-persona",
    moduleId: "students-families",
    allowedPersonas: STAFF_ROUTE_PERSONAS,
    reasonWhenDenied:
      "Students & Families report entities are restricted to staff-side school personas.",
  },
  perm_view_financial_info: {
    permission: "perm_view_financial_info",
    source: "claim",
    moduleId: "tuition",
    allowedPersonas: STAFF_ROUTE_PERSONAS,
    reasonWhenDenied:
      "Tuition and billing entities require the explicit perm_view_financial_info claim.",
  },
  perm_view_admissions: {
    permission: "perm_view_admissions",
    source: "derived-persona",
    moduleId: "admissions",
    allowedPersonas: STAFF_ROUTE_PERSONAS,
    reasonWhenDenied:
      "Admissions report entities are derived from staff admissions-route access until a dedicated claim ships.",
  },
  perm_view_financial_aid: {
    permission: "perm_view_financial_aid",
    source: "derived-persona",
    moduleId: "financial-aid",
    allowedPersonas: STAFF_ROUTE_PERSONAS,
    reasonWhenDenied:
      "Financial Aid report entities are derived from staff financial-aid-route access until a dedicated claim ships.",
  },
};

const LEGACY_REPORT_GENERATION_ENTITIES: ReportGenerationEntityDefinition[] = [
  buildEntityDefinition({
    id: "families",
    label: "Families",
    permission: "perm_view_students_families",
    moduleLabel: "Students & Families",
    joinFields: ["family_id"],
    categories: [
      {
        name: "Family Info",
        fields: [
          { id: "family_name", label: "Family name", type: "text" },
          { id: "primary_contact", label: "Primary contact", type: "text" },
          { id: "email", label: "Email", type: "text" },
          { id: "phone", label: "Phone", type: "text" },
          { id: "address", label: "Address", type: "text" },
        ],
      },
      {
        name: "Enrollment",
        fields: [
          { id: "student_count", label: "Student count", type: "number" },
          {
            id: "enrollment_status",
            label: "Enrollment status",
            type: "enum",
            enumValues: ["Active", "Inactive", "Withdrawn"],
          },
          { id: "enrollment_date", label: "Enrollment date", type: "date" },
          { id: "rate_category", label: "Rate category", type: "text" },
        ],
      },
      {
        name: "Financial",
        fields: [
          { id: "total_balance", label: "Total balance", type: "number" },
          {
            id: "has_financial_aid",
            label: "Has financial aid",
            type: "boolean",
          },
        ],
      },
    ],
  }),
  buildEntityDefinition({
    id: "students",
    label: "Students",
    permission: "perm_view_students_families",
    moduleLabel: "Students & Families",
    joinFields: ["family_id", "student_id"],
    categories: [
      {
        name: "Demographics",
        fields: [
          { id: "first_name", label: "First name", type: "text" },
          { id: "last_name", label: "Last name", type: "text" },
          { id: "date_of_birth", label: "Date of birth", type: "date" },
          { id: "grade_level", label: "Grade level", type: "text" },
          {
            id: "division",
            label: "Division",
            type: "enum",
            enumValues: ["Lower School", "Middle School", "Upper School"],
          },
        ],
      },
      {
        name: "Status",
        fields: [
          {
            id: "student_status",
            label: "Status",
            type: "enum",
            enumValues: ["Active", "Inactive", "Graduated", "Withdrawn"],
          },
          { id: "start_date", label: "Start date", type: "date" },
        ],
      },
    ],
  }),
  buildEntityDefinition({
    id: "installments",
    label: "Installments",
    permission: "perm_view_financial_info",
    moduleLabel: "Tuition",
    joinFields: ["family_id", "installment_id"],
    categories: [
      {
        name: "Schedule Details",
        fields: [
          { id: "due_date", label: "Due date", type: "date" },
          { id: "plan_name", label: "Plan name", type: "text" },
          {
            id: "installment_number",
            label: "Installment number",
            type: "number",
          },
          { id: "academic_year", label: "Academic year", type: "text" },
        ],
      },
      {
        name: "Payment Status",
        fields: [
          {
            id: "payment_status",
            label: "Status",
            type: "enum",
            enumValues: ["Paid", "Partial", "Unpaid", "Overdue"],
          },
          { id: "paid_date", label: "Paid date", type: "date" },
          { id: "is_overdue", label: "Is overdue", type: "boolean" },
        ],
      },
      {
        name: "Amounts",
        fields: [
          { id: "amount_due", label: "Amount due", type: "number" },
          { id: "amount_paid", label: "Amount paid", type: "number" },
          {
            id: "balance_remaining",
            label: "Balance remaining",
            type: "number",
          },
          { id: "late_fee_amount", label: "Late fee amount", type: "number" },
        ],
      },
    ],
  }),
  buildEntityDefinition({
    id: "payments",
    label: "Payments",
    permission: "perm_view_financial_info",
    moduleLabel: "Tuition",
    joinFields: ["family_id", "payment_id"],
    categories: [
      {
        name: "Payment Info",
        fields: [
          { id: "payment_date", label: "Payment date", type: "date" },
          { id: "payment_amount", label: "Amount", type: "number" },
          {
            id: "payment_method",
            label: "Method",
            type: "enum",
            enumValues: ["ACH", "Credit Card", "Check", "Cash", "Wire"],
          },
          { id: "reference_number", label: "Reference number", type: "text" },
        ],
      },
      {
        name: "Allocation",
        fields: [
          { id: "allocated_to", label: "Allocated to", type: "text" },
          {
            id: "payment_status_p",
            label: "Status",
            type: "enum",
            enumValues: ["Completed", "Pending", "Failed", "Refunded"],
          },
        ],
      },
    ],
  }),
  buildEntityDefinition({
    id: "tuition_accounts",
    label: "Tuition Accounts",
    permission: "perm_view_financial_info",
    moduleLabel: "Tuition",
    joinFields: ["family_id"],
    categories: [
      {
        name: "Account Summary",
        fields: [
          { id: "annual_tuition", label: "Annual tuition", type: "number" },
          { id: "total_charged", label: "Total charged", type: "number" },
          { id: "total_paid_ta", label: "Total paid", type: "number" },
          { id: "account_balance", label: "Account balance", type: "number" },
          {
            id: "account_status",
            label: "Account status",
            type: "enum",
            enumValues: ["Current", "Paid in Full", "Overdue", "Delinquent"],
          },
        ],
      },
    ],
  }),
  buildEntityDefinition({
    id: "ledger_entries",
    label: "Ledger Entries",
    permission: "perm_view_financial_info",
    moduleLabel: "Tuition",
    joinFields: ["family_id"],
    categories: [
      {
        name: "Entry Details",
        fields: [
          { id: "entry_date", label: "Entry date", type: "date" },
          {
            id: "entry_type",
            label: "Type",
            type: "enum",
            enumValues: ["Charge", "Payment", "Credit", "Adjustment", "Refund"],
          },
          { id: "description", label: "Description", type: "text" },
          { id: "debit", label: "Debit", type: "number" },
          { id: "credit", label: "Credit", type: "number" },
          { id: "running_balance", label: "Running balance", type: "number" },
        ],
      },
    ],
  }),
  buildEntityDefinition({
    id: "contracts",
    label: "Contracts",
    permission: "perm_view_financial_info",
    moduleLabel: "Tuition",
    joinFields: ["family_id"],
    categories: [
      {
        name: "Contract Info",
        fields: [
          { id: "contract_year", label: "Academic year", type: "text" },
          {
            id: "contract_status",
            label: "Status",
            type: "enum",
            enumValues: ["Draft", "Sent", "Signed", "Expired"],
          },
          { id: "signed_date", label: "Signed date", type: "date" },
          { id: "contract_amount", label: "Contract amount", type: "number" },
        ],
      },
    ],
  }),
  buildEntityDefinition({
    id: "admissions_applications",
    label: "Applications (Admissions)",
    permission: "perm_view_admissions",
    moduleLabel: "Admissions",
    joinFields: ["family_id", "application_id"],
    categories: [
      {
        name: "Application Info",
        fields: [
          { id: "app_date", label: "Application date", type: "date" },
          {
            id: "app_status",
            label: "Status",
            type: "enum",
            enumValues: [
              "New",
              "Submitted",
              "Under Review",
              "Approved",
              "Enrolled",
              "Denied",
              "Waitlisted",
              "Withdrawn",
            ],
          },
          { id: "applying_grade", label: "Applying grade", type: "text" },
          { id: "entry_year", label: "Entry year", type: "text" },
        ],
      },
      {
        name: "Documents",
        fields: [
          { id: "docs_complete", label: "Documents complete", type: "boolean" },
          { id: "missing_docs", label: "Missing documents", type: "number" },
        ],
      },
    ],
  }),
  buildEntityDefinition({
    id: "enrollment_records",
    label: "Enrollment Records",
    permission: "perm_view_admissions",
    moduleLabel: "Admissions",
    joinFields: ["family_id", "student_id"],
    categories: [
      {
        name: "Enrollment Details",
        fields: [
          { id: "enrolled_grade", label: "Enrolled grade", type: "text" },
          { id: "enrolled_year", label: "Academic year", type: "text" },
          {
            id: "enrollment_type",
            label: "Type",
            type: "enum",
            enumValues: ["New", "Returning", "Transfer"],
          },
          { id: "enrollment_confirmed", label: "Confirmed", type: "boolean" },
        ],
      },
    ],
  }),
  buildEntityDefinition({
    id: "fa_applications",
    label: "Financial Aid Applications",
    permission: "perm_view_financial_aid",
    moduleLabel: "Financial Aid",
    joinFields: ["family_id"],
    categories: [
      {
        name: "Application Info",
        fields: [
          { id: "fa_ref_number", label: "Reference number", type: "text" },
          { id: "fa_submission_date", label: "Submission date", type: "date" },
          {
            id: "fa_status",
            label: "Status",
            type: "enum",
            enumValues: [
              "New",
              "Submitted",
              "Under Review",
              "Approved",
              "Denied",
            ],
          },
          { id: "days_pending", label: "Days pending", type: "number" },
        ],
      },
      {
        name: "Family Financials",
        fields: [
          { id: "reported_income", label: "Reported income", type: "number" },
          {
            id: "income_per_child",
            label: "Income per eligible child",
            type: "number",
          },
          {
            id: "expense_ratio",
            label: "Expense-to-income ratio",
            type: "number",
          },
        ],
      },
      {
        name: "Recommendation",
        fields: [
          {
            id: "system_rec_pct",
            label: "System recommendation %",
            type: "number",
          },
          {
            id: "confidence_level",
            label: "Confidence level",
            type: "enum",
            enumValues: ["High", "Medium", "Low"],
          },
          { id: "override_amount", label: "Override amount", type: "number" },
        ],
      },
      {
        name: "Flags",
        fields: [
          { id: "flag_count", label: "Flag count", type: "number" },
          {
            id: "high_severity_flags",
            label: "High-severity flag count",
            type: "number",
          },
        ],
      },
    ],
  }),
  buildEntityDefinition({
    id: "scholarships",
    label: "Scholarships",
    permission: "perm_view_financial_aid",
    moduleLabel: "Financial Aid",
    joinFields: ["family_id", "student_id"],
    categories: [
      {
        name: "Award Details",
        fields: [
          { id: "scholarship_name", label: "Scholarship name", type: "text" },
          { id: "award_amount", label: "Award amount", type: "number" },
          { id: "award_pct", label: "Award percentage", type: "number" },
          { id: "award_year", label: "Academic year", type: "text" },
          {
            id: "award_status",
            label: "Status",
            type: "enum",
            enumValues: ["Pending", "Approved", "Disbursed", "Revoked"],
          },
        ],
      },
    ],
  }),
  buildEntityDefinition({
    id: YSTNJ_SCHOLARSHIP_WORKSHEET_ENTITY_ID,
    label: "Scholarship Worksheet Rows",
    permission: "perm_view_financial_aid",
    moduleLabel: "Financial Aid",
    authorityScope: "follow-up",
    authorityStatus: "backed",
    fixedFieldSelection: true,
    joinFields: ["family_id"],
    categories: [
      {
        name: "Worksheet Columns",
        fields: YSTNJ_SCHOLARSHIP_WORKSHEET_COLUMNS.map((column) => ({
          id: column.fieldId,
          label: column.header,
          type: worksheetColumnKindToFieldType(column.kind),
          sortable: column.fieldId !== "scholarship_worksheet_memo",
        })),
      },
    ],
  }),
  buildEntityDefinition({
    id: "notes",
    label: "Notes",
    permission: "perm_view_students_families",
    moduleLabel: "Students & Families",
    joinFields: ["family_id"],
    categories: [
      {
        name: "Note Details",
        fields: [
          { id: "note_date", label: "Date", type: "date" },
          { id: "note_author", label: "Author", type: "text" },
          { id: "note_category", label: "Category", type: "text" },
          { id: "note_content", label: "Content", type: "text" },
        ],
      },
    ],
  }),
  buildEntityDefinition({
    id: "documents",
    label: "Documents",
    permission: "perm_view_students_families",
    moduleLabel: "Students & Families",
    joinFields: ["family_id"],
    categories: [
      {
        name: "Document Info",
        fields: [
          { id: "doc_name", label: "Document name", type: "text" },
          { id: "doc_type", label: "Type", type: "text" },
          { id: "upload_date", label: "Upload date", type: "date" },
          {
            id: "doc_status",
            label: "Status",
            type: "enum",
            enumValues: ["Pending", "Approved", "Rejected"],
          },
        ],
      },
    ],
  }),
];

const LEGACY_REPORT_GENERATION_GALLERY_TEMPLATES: ReportGenerationTemplateDefinition[] =
  [
    {
      id: "aging-report",
      name: "Aging Report",
      description: "Outstanding balances by 30/60/90/120+ day buckets",
      moduleLabel: "Tuition",
      entities: ["installments", "families"],
      fields: [
        "family_name",
        "due_date",
        "amount_due",
        "balance_remaining",
        "is_overdue",
        "academic_year",
      ],
    },
    {
      id: "collection-summary",
      name: "Collection Summary",
      description: "Total collections by period with payment method breakdown",
      moduleLabel: "Tuition",
      entities: ["payments"],
      fields: ["payment_date", "payment_amount", "payment_method"],
    },
    {
      id: "payment-history",
      name: "Payment History",
      description: "Complete payment transaction log by family",
      moduleLabel: "Tuition",
      entities: ["payments", "families"],
      fields: [
        "family_name",
        "payment_date",
        "payment_amount",
        "payment_method",
        "reference_number",
      ],
    },
    {
      id: "outstanding-balances",
      name: "Outstanding Balances",
      description: "Families with unpaid balances and account status",
      moduleLabel: "Tuition",
      entities: ["tuition_accounts", "families"],
      fields: [
        "family_name",
        "account_balance",
        "account_status",
        "annual_tuition",
      ],
    },
    {
      id: "apps-by-status",
      name: "Applications by Status",
      description: "Financial aid applications grouped by current status",
      moduleLabel: "Financial Aid",
      entities: ["fa_applications", "families"],
      fields: [
        "family_name",
        "fa_ref_number",
        "fa_status",
        "fa_submission_date",
        "days_pending",
      ],
    },
    {
      id: "awards-by-grade",
      name: "Awards by Grade Level",
      description: "Scholarship awards broken down by student grade level",
      moduleLabel: "Financial Aid",
      entities: ["scholarships", "students"],
      fields: [
        "first_name",
        "last_name",
        "grade_level",
        "scholarship_name",
        "award_amount",
      ],
    },
    {
      id: "total-committed",
      name: "Total Dollars Committed",
      description: "Aggregate financial aid commitments for the academic year",
      moduleLabel: "Financial Aid",
      entities: ["scholarships"],
      fields: [
        "scholarship_name",
        "award_amount",
        "award_year",
        "award_status",
      ],
    },
    {
      id: "avg-by-income",
      name: "Average % by Income Bracket",
      description: "Average aid percentage by family income bracket",
      moduleLabel: "Financial Aid",
      entities: ["fa_applications", "scholarships"],
      fields: ["reported_income", "system_rec_pct", "award_pct"],
    },
    {
      id: "processing-time",
      name: "Processing Time Metrics",
      description: "Average days from submission to decision by period",
      moduleLabel: "Financial Aid",
      entities: ["fa_applications"],
      fields: [
        "fa_ref_number",
        "fa_submission_date",
        "fa_status",
        "days_pending",
      ],
    },
    {
      id: "doc-compliance",
      name: "Document Compliance Rate",
      description: "Percentage of applications with complete documentation",
      moduleLabel: "Financial Aid",
      entities: ["fa_applications", "documents"],
      fields: ["fa_ref_number", "fa_status", "doc_status"],
    },
    {
      id: "override-frequency",
      name: "Override Frequency",
      description: "How often staff override system aid recommendations",
      moduleLabel: "Financial Aid",
      entities: ["fa_applications"],
      fields: [
        "fa_ref_number",
        "system_rec_pct",
        "override_amount",
        "confidence_level",
      ],
    },
    {
      id: "appeal-outcomes",
      name: "Appeal Outcomes",
      description: "Results of financial aid appeal decisions",
      moduleLabel: "Financial Aid",
      entities: ["fa_applications"],
      fields: [
        "fa_ref_number",
        "fa_status",
        "reported_income",
        "override_amount",
      ],
    },
    {
      id: YSTNJ_SCHOLARSHIP_WORKSHEET_TEMPLATE_ID,
      name: "YSTNJ Scholarship Worksheet",
      description:
        "Family-level scholarship worksheet matching the 2025-2026 office review workbook columns",
      moduleLabel: "Cross-Module",
      authorityScope: "follow-up",
      authorityStatus: "backed",
      entities: [YSTNJ_SCHOLARSHIP_WORKSHEET_ENTITY_ID],
      fields: YSTNJ_SCHOLARSHIP_WORKSHEET_COLUMNS.map(
        (column) => column.fieldId,
      ),
      sorts: [
        {
          fieldId: "scholarship_worksheet_last_name",
          fieldLabel: "Last Name",
          direction: "asc",
        },
      ],
    },
    {
      id: "app-pipeline",
      name: "Application Pipeline",
      description: "All admissions applications by stage and entry year",
      moduleLabel: "Admissions",
      entities: ["admissions_applications"],
      fields: ["app_date", "app_status", "applying_grade", "entry_year"],
    },
    {
      id: "enrollment-conversion",
      name: "Enrollment Conversion",
      description: "Conversion rates from application to enrollment",
      moduleLabel: "Admissions",
      entities: ["admissions_applications", "enrollment_records"],
      fields: ["app_status", "applying_grade", "enrollment_confirmed"],
    },
    {
      id: "incomplete-docs",
      name: "Incomplete Documents",
      description: "Applications missing required documentation",
      moduleLabel: "Admissions",
      entities: ["admissions_applications"],
      fields: ["app_date", "app_status", "docs_complete", "missing_docs"],
    },
    {
      id: "demographics",
      name: "Demographics Summary",
      description: "Student demographics by grade, division, and status",
      moduleLabel: "Students & Families",
      entities: ["students"],
      fields: [
        "first_name",
        "last_name",
        "grade_level",
        "division",
        "student_status",
      ],
    },
    {
      id: "enrollment-by-grade",
      name: "Enrollment by Grade",
      description: "Current enrollment counts by grade level",
      moduleLabel: "Students & Families",
      entities: ["students"],
      fields: ["grade_level", "division", "student_status", "start_date"],
    },
    {
      id: "contact-directory",
      name: "Contact Directory",
      description: "Family contact information directory",
      moduleLabel: "Students & Families",
      entities: ["families"],
      fields: ["family_name", "primary_contact", "email", "phone", "address"],
    },
    {
      id: "fa-tuition-recon",
      name: "FA vs. Tuition Reconciliation",
      description: "Reconcile financial aid awards against tuition charges",
      moduleLabel: "Cross-Module",
      entities: ["scholarships", "tuition_accounts", "families"],
      fields: [
        "family_name",
        "award_amount",
        "annual_tuition",
        "account_balance",
      ],
    },
    {
      id: "new-family-onboarding",
      name: "New Family Onboarding Status",
      description: "Track onboarding completion for newly enrolled families",
      moduleLabel: "Cross-Module",
      entities: ["families", "admissions_applications", "contracts"],
      fields: [
        "family_name",
        "app_status",
        "contract_status",
        "enrollment_status",
      ],
    },
  ];

const GENERATED_TUITION_ENTITY_IDS: ReadonlySet<string> = new Set(
  TUITION_REPORT_GENERATION_ENTITY_AUTHORITY_INPUTS.map((entity) => entity.id),
);
const GENERATED_TUITION_TEMPLATE_IDS: ReadonlySet<string> = new Set(
  TUITION_REPORT_GENERATION_TEMPLATE_AUTHORITY_INPUTS.map(
    (template) => template.id,
  ),
);

const buildGeneratedEntityDefinition = (
  entity: (typeof TUITION_REPORT_GENERATION_ENTITY_AUTHORITY_INPUTS)[number],
): ReportGenerationEntityDefinition =>
  buildEntityDefinition({
    ...entity,
    joinFields: entity.joinFields ? [...entity.joinFields] : undefined,
    categories: entity.categories.map((category) => ({
      name: category.name,
      fields: category.fields.map((field) => ({
        ...field,
        allowedOperators: [...field.authority.allowedOperators],
        sortable: field.authority.sortSupported,
        selectable: true,
      })),
    })),
  });

const markLegacyEntityDefinition = (
  entity: ReportGenerationEntityDefinition,
): ReportGenerationEntityDefinition => ({
  ...entity,
  authorityScope: entity.authorityScope ?? "follow-up",
  authorityStatus: entity.authorityStatus ?? "legacy-unverified",
  categories: entity.categories.map((category) => ({
    ...category,
    fields: category.fields.map((field) => ({
      ...field,
      selectable: field.selectable ?? true,
    })),
  })),
});

export const REPORT_GENERATION_ENTITIES: ReportGenerationEntityDefinition[] = [
  ...TUITION_REPORT_GENERATION_ENTITY_AUTHORITY_INPUTS.map(
    buildGeneratedEntityDefinition,
  ),
  ...LEGACY_REPORT_GENERATION_ENTITIES.filter(
    (entity) => !GENERATED_TUITION_ENTITY_IDS.has(entity.id),
  ).map(markLegacyEntityDefinition),
];

export const REPORT_GENERATION_GALLERY_TEMPLATES: ReportGenerationTemplateDefinition[] =
  [
    ...TUITION_REPORT_GENERATION_TEMPLATE_AUTHORITY_INPUTS.map(
      (template): ReportGenerationTemplateDefinition => {
        const authorityTemplate: ReportGenerationAuthorityTemplateInput =
          template;
        return {
          ...template,
          entities: [...template.entities],
          fields: [...template.fields],
          conditions: authorityTemplate.conditions?.map((condition) => ({
            ...condition,
          })),
          sorts: authorityTemplate.sorts?.map((sort) => ({ ...sort })),
        };
      },
    ),
    ...LEGACY_REPORT_GENERATION_GALLERY_TEMPLATES.filter(
      (template) => !GENERATED_TUITION_TEMPLATE_IDS.has(template.id),
    ).map((template) => ({
      ...template,
      authorityScope: template.authorityScope ?? "follow-up",
      authorityStatus: template.authorityStatus ?? "legacy-unverified",
    })),
  ];

export const REPORT_GENERATION_ENTITY_JOINS: Record<
  string,
  Record<string, string>
> = {
  families: {
    students: "Family ID",
    installments: "Family ID",
    payments: "Family ID",
    tuition_accounts: "Family ID",
    ledger_entries: "Family ID",
    contracts: "Family ID",
    admissions_applications: "Family ID",
    enrollment_records: "Family ID",
    fa_applications: "Family ID",
    scholarships: "Family ID",
    notes: "Family ID",
    documents: "Family ID",
  },
  students: {
    families: "Family ID",
    scholarships: "Student ID",
    enrollment_records: "Student ID",
  },
  installments: { families: "Family ID", payments: "Installment ID" },
  payments: { families: "Family ID", installments: "Installment ID" },
};

export const REPORT_GENERATION_MODULE_COLORS: Record<string, string> = {
  Tuition: "#2697a2",
  "Financial Aid": "#237062",
  Admissions: "#FF5F5F",
  "Students & Families": "#404f4d",
  "Cross-Module": "#08bfae",
};

export const REPORT_GENERATION_ENTITIES_BY_ID = Object.freeze(
  Object.fromEntries(
    REPORT_GENERATION_ENTITIES.map((entity) => [entity.id, entity]),
  ),
) as Readonly<Record<string, ReportGenerationEntityDefinition>>;

export const REPORT_GENERATION_TEMPLATES_BY_ID = Object.freeze(
  Object.fromEntries(
    REPORT_GENERATION_GALLERY_TEMPLATES.map((template) => [
      template.id,
      template,
    ]),
  ),
) as Readonly<Record<string, ReportGenerationTemplateDefinition>>;

const normalizeGrant = (value: unknown): string =>
  String(value ?? "")
    .trim()
    .toLowerCase();

export const hasReportGenerationCognitoPermission = (
  profile: ReportGenerationCognitoProfile | null | undefined,
  permission: string,
): boolean => {
  const expected = normalizeGrant(permission);
  const grants = [
    ...(profile?.permissions ?? []),
    ...(profile?.groups ?? []),
  ].map(normalizeGrant);
  return grants.includes(expected);
};

export const resolveReportGenerationPermission = (
  permission: string,
  input: {
    profile: ReportGenerationCognitoProfile | null;
    effectivePersona: ReportGenerationShellPersona;
  },
): ReportGenerationPermissionResolution => {
  const policy =
    REPORT_GENERATION_PERMISSION_POLICIES[
      permission as ReportGenerationPermissionKey
    ];
  if (!policy) {
    return {
      permission,
      granted: false,
      status: "unmodeled",
      source: "unmodeled",
      moduleId: null,
      routeRoot: null,
      allowedPersonas: [],
      reason: `No owned permission policy is mapped for ${permission}.`,
    };
  }

  const module = requireModule(policy.moduleId);
  if (policy.source === "claim") {
    const granted = hasReportGenerationCognitoPermission(
      input.profile,
      policy.permission,
    );
    return {
      permission,
      granted,
      status: granted ? "granted-claim" : "denied-claim",
      source: "claim",
      moduleId: policy.moduleId,
      routeRoot: module.routeRoot,
      allowedPersonas: policy.allowedPersonas,
      reason: granted
        ? `${permission} is present on the active Cognito session.`
        : policy.reasonWhenDenied,
    };
  }

  const granted = policy.allowedPersonas.includes(input.effectivePersona);
  return {
    permission,
    granted,
    status: granted ? "granted-derived" : "denied-derived",
    source: "derived-persona",
    moduleId: policy.moduleId,
    routeRoot: module.routeRoot,
    allowedPersonas: policy.allowedPersonas,
    reason: granted
      ? `${permission} is derived from ${policy.moduleId} access for the ${input.effectivePersona} persona.`
      : policy.reasonWhenDenied,
  };
};

export const resolveReportGenerationEntityAccess = (input: {
  profile: ReportGenerationCognitoProfile | null;
  effectivePersona: ReportGenerationShellPersona;
}): ReportGenerationEntityAccess[] =>
  REPORT_GENERATION_ENTITIES.map((entity) => {
    const permissionResolution = resolveReportGenerationPermission(
      entity.permission,
      input,
    );
    return {
      ...entity,
      permissionResolution,
      accessible: permissionResolution.granted,
    };
  });

export const resolveReportGenerationTemplateAccess = (input: {
  profile: ReportGenerationCognitoProfile | null;
  effectivePersona: ReportGenerationShellPersona;
}): ReportGenerationTemplateAccess[] => {
  const entitiesById = Object.fromEntries(
    resolveReportGenerationEntityAccess(input).map((entity) => [
      entity.id,
      entity,
    ]),
  ) as Record<string, ReportGenerationEntityAccess>;

  return REPORT_GENERATION_GALLERY_TEMPLATES.map((template) => {
    const blockedEntities = template.entities
      .map((entityId) => entitiesById[entityId] ?? null)
      .filter((entity): entity is ReportGenerationEntityAccess =>
        Boolean(entity),
      )
      .filter((entity) => !entity.accessible);
    return {
      ...template,
      accessible: blockedEntities.length === 0,
      blockedEntityIds: blockedEntities.map((entity) => entity.id),
      blockedReasons: blockedEntities.map(
        (entity) => entity.permissionResolution.reason,
      ),
    };
  });
};
